/**
 * Shared visibility helper.
 *
 * Single source of truth for "is this field/section visible given the current
 * form state". Used by both the renderer (ConditionalR) and the validator
 * (validateForm) so they cannot disagree about whether a hidden field still
 * counts as required.
 *
 * The renderer hides fields with appears_when when the expression evaluates
 * false. The validator must skip those fields when checking required — a
 * hidden field is never required (otherwise the user gets a blocking error
 * for a field they cannot see, e.g. the "I don't know" parent gate on the
 * 47SP family entries).
 */

import { parseExpression } from './parser.js';
import { evaluate } from './evaluator.js';

/**
 * Evaluate a boolean expression string against form state. Returns true
 * (visible) for null/empty expressions. Fails open on parse/eval errors so
 * a malformed expression never accidentally hides a field.
 *
 * @param expression - The boolean expression from schema (appears_when /
 *                     conditional_on / section.appears_when), or null
 * @param state      - The current form values (flat or nested — the
 *                     evaluator handles both)
 */
export function isVisible(
  expression: string | null | undefined,
  state: Record<string, unknown>,
): boolean {
  if (!expression) return true;
  try {
    const ast = parseExpression(expression);
    if (!ast) return true;
    return evaluate(ast, state);
  } catch {
    // Fail open — a broken expression should never silently hide a field.
    return true;
  }
}

/**
 * Build an entry-scoped state for evaluating a field inside a repeater.
 *
 * Schema expressions reference repeater fields with `[*]` wildcards
 * (e.g. `applicant.immediate_family.entries[*].information_unknown`),
 * because the schema is shared across all entries. When validating one
 * specific entry, those wildcards must resolve to that entry's value, not
 * to the array of all entries' values.
 *
 * This helper takes the original state plus the repeater's array path and
 * the index being validated, and returns a new state where the wildcarded
 * canonical paths are added as direct flat-lookup keys mapping to the
 * specific entry's values. The evaluator's flat-key lookup will then hit
 * those, so an expression like `entries[*].information_unknown != "Yes"`
 * resolves against entry N's value.
 *
 * @param state         - The original form state
 * @param repeaterPath  - Dot path to the repeater array, e.g.
 *                        `"applicant.immediate_family.entries"`
 * @param index         - The entry index being validated
 */
export function withEntryContext(
  state: Record<string, unknown>,
  repeaterPath: string,
  index: number,
): Record<string, unknown> {
  const entry = readEntry(state, repeaterPath, index);
  if (entry === undefined || entry === null) return state;

  const scoped: Record<string, unknown> = { ...state };
  const wildcardPrefix = `${repeaterPath}[*]`;

  if (typeof entry === 'object' && !Array.isArray(entry)) {
    for (const [k, v] of Object.entries(entry as Record<string, unknown>)) {
      scoped[`${wildcardPrefix}.${k}`] = v;
    }
  }
  // Also expose the entry itself under the wildcard prefix so expressions
  // that reference the whole entry resolve.
  scoped[wildcardPrefix] = entry;
  return scoped;
}

/**
 * Scope form state to a repeater entry given the renderer's name prefix
 * (e.g. `"sponsor.immediate_family.entries.0"`). The prefix is what
 * RepeaterR passes to entry fields as namePrefix: the repeater array path
 * plus the entry index. Non-entry prefixes (no trailing index) and absent
 * prefixes return the state unchanged.
 *
 * This is how ConditionalR resolves `[*]` wildcards for the entry being
 * rendered — the same withEntryContext used by validateForm, so the
 * renderer and the validator cannot disagree about entry-field visibility.
 */
export function scopeToEntryPrefix(
  state: Record<string, unknown>,
  entryPrefix: string | undefined,
): Record<string, unknown> {
  if (!entryPrefix) return state;
  const match = /^(.+)\.(\d+)$/.exec(entryPrefix);
  if (!match) return state;
  return withEntryContext(state, match[1], Number(match[2]));
}

function readEntry(
  state: Record<string, unknown>,
  repeaterPath: string,
  index: number,
): unknown {
  // Try flat repeater key first (the array as a single value).
  const direct = state[repeaterPath];
  if (Array.isArray(direct)) return direct[index];

  // Try flat indexed key (e.g. "applicant.foo.entries.0").
  const flatIdxKey = `${repeaterPath}.${index}`;
  if (Object.prototype.hasOwnProperty.call(state, flatIdxKey)) {
    return state[flatIdxKey];
  }

  // Walk a nested object structure.
  const segments = repeaterPath.split('.');
  let current: unknown = state;
  for (const seg of segments) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object' || Array.isArray(current)) return undefined;
    current = (current as Record<string, unknown>)[seg];
  }
  if (Array.isArray(current)) return current[index];
  return undefined;
}
